import { A } from '@solidjs/router';
import { For, Show, createEffect, createResource, createSignal } from 'solid-js';
import { api, Customer, Station } from '../api';
import { isOperator } from '../me';
import { ErrorLine, StatusPill, useAction } from '../ui';

type Genre = { slug: string; name: string; customer_facing: boolean };

/** A customer's Studio stations: the limit (set by an operator), the stations, and a new one within the limit. The
 *  station itself is run from the Studio console; operators also reach it under Radio. */
export default function CustomerStations(props: { customer: Customer; admin: boolean; onChanged: (c: Customer) => void }) {
  const [stations, { refetch }] = createResource(() => props.customer.id,
    async (id) => (await api<Station[]>('/studio/stations')).filter((s) => s.customer_id === id));
  const [genres] = createResource(() => api<Genre[]>('/library/genres'));
  const [limit, setLimit] = createSignal(0);
  createEffect(() => setLimit(props.customer.station_limit));
  const [f, setF] = createSignal({ name: '', slug: '', genre: '' });
  const a = useAction();
  const room = () => (stations()?.length ?? 0) < props.customer.station_limit;

  const saveLimit = (e: Event) => {
    e.preventDefault();
    a.run(async () => props.onChanged(await api<Customer>(`/customers/${props.customer.id}`, { method: 'PATCH',
      json: { station_limit: limit() } })));
  };
  const setDj = (on: boolean) => a.run(async () => props.onChanged(await api<Customer>(`/customers/${props.customer.id}`,
    { method: 'PATCH', json: { dj_allowed: on } })));
  const create = (e: Event) => {
    e.preventDefault();
    const v = f();
    a.run(async () => {
      await api('/studio/stations', { method: 'POST', json: { customer_id: props.customer.id, name: v.name, slug: v.slug,
        genre_mix: [{ genre: v.genre, weight: 100 }] } });
      setF({ name: '', slug: '', genre: '' });
      refetch();
      props.onChanged({ ...props.customer, station_count: props.customer.station_count + 1 });
    });
  };
  // a slug from the name, until the slug is typed by hand
  const slugOf = (name: string) => name.toLowerCase().normalize('NFKD').replace(/[^a-z0-9]+/g, '-').replace(/^[^a-z]+|-+$/g, '').slice(0, 32);

  return (
    <Show when={isOperator() || props.customer.station_limit > 0 || (stations()?.length ?? 0) > 0}>
      <section aria-label="Stations" class="mb-8">
        <div class="mb-3 flex flex-wrap items-center gap-3">
          <h2 class="wordmark text-xl">Stations</h2>
          <span class="mono text-ink-3">{stations()?.length ?? 0} of {props.customer.station_limit}</span>
          <Show when={isOperator()}>
            <form class="ml-auto flex items-center gap-2" onSubmit={saveLimit}>
              <label class="text-sm text-ink-2" for="station-limit">Limit</label>
              <input id="station-limit" class="input input-sm input-bordered w-20" type="number" min="0" max="100" value={limit()}
                onInput={(e) => setLimit(Number(e.currentTarget.value))} />
              <button class="btn btn-sm" disabled={a.busy() || limit() === props.customer.station_limit}>Save</button>
            </form>
            <label class="flex items-center gap-2 text-sm text-ink-2">
              <input type="checkbox" class="toggle toggle-sm" checked={props.customer.dj_allowed} disabled={a.busy()}
                onChange={(e) => setDj(e.currentTarget.checked)} />
              DJ</label>
          </Show>
          <Show when={!isOperator() && props.customer.dj_allowed}><StatusPill tone="ok">DJ</StatusPill></Show>
        </div>
        <ErrorLine error={a.error} />
        <div class="card-z overflow-x-auto p-0">
          <table class="table">
            <thead><tr><th class="th">Station</th><th class="th">Stream</th><th class="th">Now</th><th class="th">Status</th></tr></thead>
            <tbody>
              <For each={stations()} fallback={<tr><td colspan="4" class="text-ink-3">No stations yet.</td></tr>}>
                {(s) => (
                  <tr class="hover">
                    <td><Show when={isOperator()} fallback={<span class="font-semibold">{s.name}</span>}>
                      <A href={`/radio/${s.id}`} class="font-semibold hover:text-magenta">{s.name}</A></Show></td>
                    <td class="mono text-sm">play.scai.fm{s.mount}</td>
                    <td class="text-ink-2">{s.on_air.name}<Show when={s.status === 'on_air'}> · {s.listeners} listening</Show></td>
                    <td><StatusPill tone={s.status === 'on_air' ? 'ok' : s.status === 'maintenance' ? 'bad' : 'off'}>
                      {s.status === 'on_air' ? 'On air' : s.status === 'maintenance' ? 'Taken down' : 'Off air'}</StatusPill></td>
                  </tr>
                )}
              </For>
            </tbody>
          </table>
        </div>
        <Show when={props.admin && room()}>
          <form class="mt-3 flex flex-wrap items-end gap-2" onSubmit={create}>
            <label class="form-control"><span class="label-text font-semibold">Name</span>
              <input class="input input-sm input-bordered" required maxLength={120} placeholder="e.g. Corner FM" value={f().name}
                onInput={(e) => { const name = e.currentTarget.value; setF({ ...f(), name, slug: f().slug && f().slug !== slugOf(f().name) ? f().slug : slugOf(name) }); }} /></label>
            <label class="form-control"><span class="label-text font-semibold">Address</span>
              <span class="flex items-center gap-1"><span class="mono text-sm text-ink-3">/s/</span>
                <input class="input input-sm input-bordered mono" required pattern="[a-z][a-z0-9-]{2,31}" value={f().slug}
                  onInput={(e) => setF({ ...f(), slug: e.currentTarget.value })} aria-label="Address" /></span></label>
            <label class="form-control"><span class="label-text font-semibold">Plays</span>
              <select class="select select-sm select-bordered" required value={f().genre} onChange={(e) => setF({ ...f(), genre: e.currentTarget.value })}>
                <option value="" disabled>Pick a genre</option>
                <For each={genres()?.filter((g) => g.customer_facing)}>{(g) => <option value={g.slug}>{g.name}</option>}</For>
              </select></label>
            <button class="btn btn-sm btn-primary" disabled={a.busy()}>New station</button>
          </form>
          <p class="mt-1 text-xs text-ink-3">A new station is off air and plays songs only; more genres and a schedule come in the Studio console.</p>
        </Show>
      </section>
    </Show>
  );
}
